import { useState, useRef, useEffect } from "react";

const EditableSection = ({
  title,
  value,
  originalValue,
  onSave,
  onRevert,
  type = "textarea",
  renderView,
  renderEdit,
  placeholder = "Enter text...",
  rows = 4,
  className = "",
  titleClassName = "",
  titleIcon,
  showTitle = true,
  viewClassName = "",
  isEditable = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const inputRef = useRef(null);

  const hasChanges = JSON.stringify(value) !== JSON.stringify(originalValue);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || "");
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.setSelectionRange && typeof editValue === 'string') {
        const len = editValue.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }
  }, [isEditing]);

  const handleSave = () => {
    onSave && onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setIsEditing(false);
  };

  const handleRevert = () => {
    if (window.confirm("Are you sure you want to revert to the original? Your changes will be lost.")) {
      onRevert && onRevert();
    }
  };

  const renderEditInput = () => {
    if (type === "custom" && renderEdit) {
      return renderEdit({
        value: editValue,
        onChange: setEditValue,
        inputRef,
        onSave: handleSave,
        onCancel: handleCancel
      });
    }

    if (type === "text") {
      return (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Escape") handleCancel();
          }}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-700"
          placeholder={placeholder}
        />
      );
    }

    return (
      <textarea
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") handleCancel();
        }}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-gray-600 leading-relaxed"
        rows={rows}
        placeholder={placeholder}
      />
    );
  };

  const renderViewContent = () => {
    if (type === "custom" && renderView) {
      return renderView({ value });
    }

    return (
      <p className={`text-gray-600 leading-relaxed ${viewClassName}`}>
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </p>
    );
  };

  return (
    <div className={className}>
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-2xl font-bold text-gray-900 flex items-center gap-2 ${titleClassName}`}>
            {titleIcon}
            {title}
          </h2>
          <div className="flex items-center gap-2">
            {isEditable && hasChanges && !isEditing && (
              <button
                onClick={handleRevert}
                className="p-2 text-amber-500 cursor-pointer hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                title="Revert to original"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                </svg>
              </button>
            )}
            {isEditable && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 text-gray-400 cursor-pointer hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                title={`Edit ${title?.toLowerCase() || 'content'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {isEditing ? (
        <div className="space-y-3">
          {renderEditInput()}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className="px-4 py-2 cursor-pointer bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 cursor-pointer bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        renderViewContent()
      )}
    </div>
  );
};

export const InlineEditable = ({
  value,
  originalValue,
  onSave,
  onRevert,
  className = "",
  inputClassName = "",
  placeholder = "Enter text..."
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const inputRef = useRef(null);

  const hasChanges = value !== originalValue;

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || "");
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    onSave && onSave(editValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleSave();
          if (e.key === "Escape") handleCancel();
        }}
        className={`px-2 py-1 border border-teal-400 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm ${inputClassName}`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <span className={`group inline-flex items-center gap-1 ${className}`}>
      <span
        onClick={() => setIsEditing(true)}
        className="cursor-pointer hover:bg-teal-50 rounded px-1 -mx-1 transition-colors"
      >
        {value || <span className="text-gray-400 italic">{placeholder}</span>}
      </span>
      {hasChanges && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRevert && onRevert();
          }}
          className="opacity-0 group-hover:opacity-100 p-0.5 text-amber-500 hover:text-amber-600 transition-opacity"
          title="Revert"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>
      )}
      <svg
        className="w-3 h-3 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        onClick={() => setIsEditing(true)}
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    </span>
  );
};

export default EditableSection;
